import { AbstractControl } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { find, get, toLower } from 'lodash';

export function minOrNull(control: AbstractControl, field: FormlyFieldConfig) {
  const validation = find(field.validators?.validation, {
    name: 'minOrNull',
  });

  if (validation && control.value && control.value < validation.options.min) {
    return { minOrNull: true };
  }

  return null;
}

export function minLengthLowercase(
  control: AbstractControl,
  field: FormlyFieldConfig
) {
  const min = get(field.props, 'minLengthLowercase') || 1;
  const regExp = new RegExp(`^(?=.*[a-z].{${min - 1},}).*$`);

  return !regExp.test(control.value) || !control.value
    ? null
    : { lowercaseChars: true };
}

export function minLengthUppercase(
  control: AbstractControl,
  field: FormlyFieldConfig
) {
  const min = get(field.props, 'minLengthUppercase') || 1;
  const regExp = new RegExp(`^(?=.*[A-Z].{${min - 1},}).*$`);

  return !regExp.test(control.value) || !control.value
    ? null
    : { uppercaseChars: true };
}

export function minLengthNumeric(
  control: AbstractControl,
  field: FormlyFieldConfig
) {
  const min = get(field.props, 'minLengthNumeric') || 1;
  const regExp = new RegExp(`^(?=.*[0-9].{${min - 1},}).*$`);

  return !regExp.test(control.value) || !control.value
    ? null
    : { numberChars: true };
}

export function minLengthSpecial(
  control: AbstractControl,
  field: FormlyFieldConfig
) {
  const min = get(field.props, 'minLengthSpecial') || 1;
  const regExp = new RegExp(`^(?=.*[£_\\-=!@#$%^&*].{${min - 1},}).*$`);

  return !regExp.test(control.value) || !control.value
    ? null
    : { minLengthSpecial: true };
}

export function noSpaces(control: AbstractControl, field: FormlyFieldConfig) {
  const regExp = /\s/;

  return regExp.test(control.value) ? null : { noSpaces: true };
}

export function onlyPasswordChars(control: AbstractControl) {
  const regExp = /^[0-9a-zA-Z£_\-=!@#$%^&*]*$/;

  return !regExp.test(control.value) || !control.value
    ? null
    : { onlyPasswordChars: true };
}

interface FieldMatchValidatorOptions {
  paths?: [string, string];
  compareStrategy?: 'strict' | 'loose';
}

export function fieldMatchValidator(
  control: AbstractControl,
  _?: FormlyFieldConfig,
  options?: FieldMatchValidatorOptions
) {
  if (!options || !options.paths) {
    console.warn('No options provided for field match validator');
    return null;
  }
  const firstValue = get(control.value, options.paths[0]);
  const secondValue = get(control.value, options.paths[1]);

  if (options.compareStrategy === 'loose') {
    if (toLower(firstValue) === toLower(secondValue)) {
      return null;
    }
  }

  return firstValue === secondValue ? null : { fieldMatch: true };
}

export interface CustomValidatorOptions {
  errorPath?: string;
  expression?: (control: AbstractControl, field: FormlyFieldConfig) => boolean;
  message?: string;
}

export function customValidator(
  control: AbstractControl,
  field: FormlyFieldConfig,
  options: CustomValidatorOptions = {}
) {
  if (!options.expression) {
    console.warn('No expression provided for custom validator');
    return null;
  }

  return options.expression(control, field)
    ? null
    : { customValidator: { message: options.message || 'Invalid' } };
}

export function fieldArrayValidation(control: AbstractControl, field: FormlyFieldConfig) {
  const value = control?.value || [];
  const isValid = value.every((item: { [i: string]: string }) =>
    field.fieldGroup?.every(formControl =>
      !formControl.props?.required ||
      (!Array.isArray(formControl.key) &&
        item[formControl.key as keyof typeof item] !== undefined &&
        item[formControl.key as keyof typeof item] !== null &&
        item[formControl.key as keyof typeof item] !== '')
    )
  );

  return isValid ? null : { fieldArrayValidation: true };
}
